import { MyDocument } from '../Document/Document';
import { usePDF } from '@react-pdf/renderer';
import convertReactDocumentToBase64 from '../../../../utils/convertReactDocumentToBase64';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  UseFormHandleSubmit,
  UseFormReturn,
  UseFormSetValue,
  useFieldArray,
  useForm,
} from '@agrotoken-tech/ui';
import {
  SignupFormState,
  legalRepresentativeDefaultElement,
  useSignupFormContext,
} from '../../context/useSignUpContext';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { CompanyLegalRepresentativeDataDto } from '../../../../services/sign-up/dto/createSignUpDto';
import { RepresentativePosition, representativePositions } from '@bcba/types';
import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormStep } from '../utils/form-step.interface';

const resolver = classValidatorResolver(CompanyLegalRepresentativeDataDto);

interface ThirdStepHook {
  handleSubmit: UseFormHandleSubmit<SignupFormState, undefined>;
  submitHandler: (data: SignupFormState) => Promise<void>;
  firstLegalRepresentativeSubTitle: string;
  formMethods: UseFormReturn<SignupFormState, any, undefined>;
  legalRepresentativesFields: Record<'id', string>[];
  legalRepresentativeKey: 'legalRepresentatives';
  setValue: UseFormSetValue<SignupFormState>;
  representativePositionOptions: {
    value: RepresentativePosition;
    label: string;
  }[];
  shouldAddLegalRepresentativeForm: boolean;
  handleAddSecondRepresentative: () => Promise<void>;
  isSubmitting: boolean;
  secondLegalRepresentativeSubTitle: string;
  handleRemoveSecondRepresentative: () => void;
}

export const useThirdStepForm = (props: FormStep): ThirdStepHook => {
  const { setStep } = props;
  const { t } = useTranslation();
  const { showErrorToast } = useToastsHook();
  const [documentName, setDocumentName] = useState('');
  const [pdfInstance, updatePDFInstance] = usePDF({
    document: undefined,
  });
  const [validatingReCaptcha, setValidatingReCaptcha] = useState(false);

  const {
    formState,
    updateForm,
    isSubmitting,
    setIsSubmitting,
    getReCaptchaToken,
    setIsDirty,
  } = useSignupFormContext();
  const formMethods = useForm<typeof formState>({
    defaultValues: formState,
    resolver,
  });

  const { enableReCaptchaInSignUp } = useFlags();

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: rhfState,
  } = formMethods;
  const legalRepresentativeKey = `legalRepresentatives`;
  const legalRepresentativeMaxLength = 2;
  const {
    fields: legalRepresentativesFields,
    append: appendLegalRepresentative,
    remove: removeLegalRepresentative,
  } = useFieldArray({
    control,
    name: legalRepresentativeKey as any,
    rules: {
      minLength: 1,
      maxLength: legalRepresentativeMaxLength,
    },
  });

  useEffect(() => {
    if (Object.keys(rhfState.dirtyFields).length > 0) {
      setIsDirty(true);
    }
  }, [rhfState.dirtyFields, setIsDirty]);

  const legalRepresentativesLength = legalRepresentativesFields.length;
  const shouldAddLegalRepresentativeForm =
    legalRepresentativesLength < legalRepresentativeMaxLength;

  const representativePositionOptions = representativePositions.map((value) => {
    return {
      value,
      label: t(`businessSignup.form.representativePosition.${value}`),
    };
  });

  const handleAddSecondRepresentative = async () => {
    const isOkStatus = await trigger();
    if (isOkStatus) {
      appendLegalRepresentative(legalRepresentativeDefaultElement);
    }
  };

  const handleRemoveSecondRepresentative = () => {
    removeLegalRepresentative(legalRepresentativesLength - 1);
  };

  const firstLegalRepresentativeSubTitle = t(
    'businessSignUp.thirdStep.stepIndicator',
  );
  const secondLegalRepresentativeSubTitle = `${firstLegalRepresentativeSubTitle} 2`;

  const submitHandler = async (data: SignupFormState) => {
    const legalRepresentatives = data[legalRepresentativeKey] || [];
    updateForm({ formState: { ...formState, legalRepresentatives } });

    // Create react document and update pdf instance
    updatePDFInstance(
      <MyDocument {...{ ...formState, legalRepresentatives }} />,
    );

    // Create react document name
    setDocumentName(`Registro_ Empresa_${formState.companyFiscalId}.pdf`);
  };

  const onFormUpdateSuccess = () => {
    setStep(4);
  };

  useEffect(() => {
    const getDocumentData = async () => {
      if (pdfInstance.blob) {
        setIsSubmitting(true);
        const permissionFileDocument = await convertReactDocumentToBase64(
          pdfInstance.blob,
          documentName,
        );

        if (formState.companyProvinceUuid) {
          delete formState['companyProvinceUuid'];
        }

        if (enableReCaptchaInSignUp) {
          setValidatingReCaptcha(true);
          getReCaptchaToken()
            .then((token) => {
              updateForm({
                formState: {
                  ...formState,
                  permissionFileDocument,
                  reCaptchaToken: token,
                },
                shouldSubmitCreation: true,
                onFormUpdateSuccess,
              });
            })
            .catch((_) => {
              showErrorToast({
                title: t('common.toast.error.title'),
                text: t('common.toast.error.reCaptcha'),
              });
            })
            .finally(() => {
              setValidatingReCaptcha(false);
            });
        } else
          updateForm({
            formState: {
              ...formState,
              permissionFileDocument,
            },
            shouldSubmitCreation: true,
            onFormUpdateSuccess,
          });
      }
    };

    getDocumentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfInstance.blob]);

  return {
    handleSubmit,
    submitHandler,
    firstLegalRepresentativeSubTitle,
    formMethods,
    legalRepresentativesFields,
    legalRepresentativeKey,
    setValue,
    representativePositionOptions,
    shouldAddLegalRepresentativeForm,
    handleAddSecondRepresentative,
    isSubmitting: isSubmitting || validatingReCaptcha,
    secondLegalRepresentativeSubTitle,
    handleRemoveSecondRepresentative,
  };
};

export default useThirdStepForm;
